import { ChevronDown, ExternalLink } from "lucide-react";
import { cn } from "@/lib/cn";
import { STATUS_META, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { serviceLabel, type IncomingOrderRow, type ServiceRow } from "../production-types";
import { MobilePlanilhaCard } from "./MobilePlanilhaCard";

type Props = {
  order: IncomingOrderRow;
  rows: ServiceRow[];
  expandido: boolean;
  onAlternarExpansao: () => void;
  selecionados: Set<string>;
  onAlternarSelecao: (serviceId: string) => void;
  expandidosServicos: Set<string>;
  onAlternarExpansaoServico: (serviceId: string) => void;
  onVerServico?: (row: ServiceRow) => void;
  onVerPedido?: (order: IncomingOrderRow) => void;
  onEditarEstagio: (row: ServiceRow) => void;
  onEditarModelo: (row: ServiceRow) => void;
  onEditarResponsavel: (row: ServiceRow) => void;
  onBaixarAnexo: (row: ServiceRow) => void;
  /** Repassado a cada card de serviço — ver MobilePlanilhaCard. */
  editavel?: boolean;
};

/**
 * Modo "Agrupar por pedido" no celular: o card é o PEDIDO. Fechado, lista os serviços em tópicos —
 * uma linha por serviço, só pra dar noção do conteúdo. Aberto, cada serviço vira o mesmo card
 * operacional da lista plana, com seleção, estágio, modelo e responsável editáveis.
 */
export function MobilePlanilhaOrderCard({
  order,
  rows,
  expandido,
  onAlternarExpansao,
  selecionados,
  onAlternarSelecao,
  expandidosServicos,
  onAlternarExpansaoServico,
  onVerServico,
  onVerPedido,
  onEditarEstagio,
  onEditarModelo,
  onEditarResponsavel,
  onBaixarAnexo,
  editavel = true,
}: Props) {
  const meta = STATUS_META[order.status];
  const servicos = rows.filter((r) => r.service);

  return (
    <li className="rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
      <button
        type="button"
        onClick={onAlternarExpansao}
        aria-expanded={expandido}
        className="w-full p-3 text-left"
      >
        <span className="flex items-start gap-2">
          <span className="min-w-0 flex-1 break-words text-sm font-bold leading-snug text-zinc-900 dark:text-foreground">
            {order.patientName}
          </span>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            {meta.label}
          </span>
          <ChevronDown
            className={cn("mt-0.5 h-4 w-4 shrink-0 text-zinc-400 transition-transform", expandido && "rotate-180")}
          />
        </span>
        <span className="mt-0.5 block truncate text-[13px] text-zinc-500 dark:text-muted-foreground">
          {!order.organization.isPersonal && `${order.organization.name} · `}
          Pedido: {formatDate(order.createdAt)} ·{" "}
          {rows.length === 1 ? "1 serviço" : `${rows.length} serviços`}
        </span>

        {!expandido && servicos.length > 0 && (
          <span className="mt-2 flex flex-col gap-1 text-[13px] text-zinc-600 dark:text-muted-foreground">
            {servicos.map((r) => (
              <span key={r.service.id} className="flex min-w-0 items-center gap-1.5">
                <span className="h-1 w-1 shrink-0 rounded-full bg-zinc-400" />
                <span className="min-w-0 truncate">{serviceLabel(r.service)}</span>
              </span>
            ))}
          </span>
        )}
      </button>

      {expandido && (
        <div className="border-t border-zinc-100 p-2 dark:border-border">
          <ul className="space-y-2">
            {rows.map((row) => (
              <MobilePlanilhaCard
                key={row.service ? row.service.id : `${order.id}-vazio`}
                row={row}
                ocultarPacienteClinica
                selecionado={row.service ? selecionados.has(row.service.id) : false}
                onAlternarSelecao={onAlternarSelecao}
                expandido={row.service ? expandidosServicos.has(row.service.id) : false}
                onAlternarExpansao={
                  row.service ? () => onAlternarExpansaoServico(row.service.id) : undefined
                }
                onVerServico={onVerServico ? () => onVerServico(row) : undefined}
                onVerPedido={undefined}
                onEditarEstagio={() => onEditarEstagio(row)}
                onEditarModelo={() => onEditarModelo(row)}
                onEditarResponsavel={() => onEditarResponsavel(row)}
                onBaixarAnexo={() => onBaixarAnexo(row)}
                editavel={editavel}
              />
            ))}
          </ul>
          {onVerPedido && (
            <button
              type="button"
              onClick={() => onVerPedido(order)}
              className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-[13px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Ver pedido completo
            </button>
          )}
        </div>
      )}
    </li>
  );
}
